// Human-readable protocol numbers, derived from @hashcurve/protocol so copy never drifts from the
// contracts' constants.
import {
  BPS,
  CURVE_CREATOR_SHARE_BPS,
  CURVE_FEE_BPS,
  CURVE_SUPPLY,
  GRADUATION_RESERVE,
  IDLE_GRACE,
  IDLE_HALFLIFE,
  LOT,
  LP_CREATOR_SHARE_BPS,
  LP_SUPPLY,
  MAX_START_DELAY,
  MAX_STEP,
  MAX_TARGET,
  N_LOTS,
  START_TARGET,
  TARGET_INTERVAL,
  TOTAL_SUPPLY,
  VIRTUAL_TOKEN_0,
} from '@hashcurve/protocol';
import { formatEth, formatWork } from './format';

const whole = (x: bigint) => (x / 10n ** 18n).toLocaleString('en-US');
const pct = (bps: bigint) => `${Number((bps * 10_000n) / BPS) / 100}%`;
const log2 = (x: bigint) => x.toString(2).length - 1;
const SUP = '⁰¹²³⁴⁵⁶⁷⁸⁹';
/** 38 -> "2³⁸" */
export const pow2 = (n: number) => `2${String(n).split('').map((d) => SUP[Number(d)]).join('')}`;

const curveFee = (CURVE_FEE_BPS * CURVE_CREATOR_SHARE_BPS) / BPS;
const multiple = Number(VIRTUAL_TOKEN_0 * 1000n / (VIRTUAL_TOKEN_0 - CURVE_SUPPLY)) / 1000;

export const P = {
  totalSupply: whole(TOTAL_SUPPLY),
  curveSupply: whole(CURVE_SUPPLY),
  curveSupplyShort: `${CURVE_SUPPLY / 10n ** 24n}M`,
  lpSupply: whole(LP_SUPPLY),
  lot: whole(LOT),
  lots: N_LOTS,
  reserve: formatEth(GRADUATION_RESERVE),
  fee: pct(CURVE_FEE_BPS),
  creatorFee: pct(curveFee),
  protocolFee: pct(CURVE_FEE_BPS - curveFee),
  lpSplit: `${pct(LP_CREATOR_SHARE_BPS).replace('%', '')}/${pct(BPS - LP_CREATOR_SHARE_BPS).replace('%', '')}`,
  interval: Number(TARGET_INTERVAL),
  grace: Number(IDLE_GRACE),
  halflife: Number(IDLE_HALFLIFE),
  maxStep: Number(MAX_STEP),
  maxStartDays: Number(MAX_START_DELAY) / 86_400,
  startLog2: 256 - log2(START_TARGET),
  floorLog2: 256 - log2(MAX_TARGET),
  /** price at the last lot / price at the first lot */
  priceMultiple: Math.round(multiple * multiple),
  startDifficulty: formatWork(2 ** (256 - log2(START_TARGET))),
};

export const FIXED_NUMBERS: [string, string][] = [
  ['Total supply', `${P.totalSupply} tokens`],
  ['On the curve', `${P.curveSupply} in ${P.lots} lots of ${P.lot}`],
  ['At graduation', `${P.lpSupply} tokens paired with the ${P.reserve} reserve`],
  ['Curve fee', `${P.fee} per trade: ${P.creatorFee} to the creator, ${P.protocolFee} to the protocol`],
  ['Creator allocation', '0 tokens; creators buy by mining like everyone else'],
  ['Proof cadence', `Difficulty retargets after every proof, aiming for one every ~${P.interval} s`],
  ['Idle decay', `After ${P.grace} s without a proof, difficulty halves every ${P.halflife} s`],
  ['Pool LP', `Held by the launch contract; only fee growth can be collected, split ${P.lpSplit} creator/protocol`],
];

/** Curve supply in lots, with one decimal when a lot is partly in circulation: 122, 0.5 */
export function lotsIn(sold: bigint): string {
  const tenths = Number((sold * 10n) / LOT);
  return tenths % 10 === 0 ? String(tenths / 10) : (tenths / 10).toFixed(1);
}

export const plural = (n: number | bigint, one: string, many = `${one}s`) => `${n} ${BigInt(n) === 1n ? one : many}`;
